/* Milano collection guide — progressive enhancement only.
   1. Scroll-spy for the sticky section nav (aria-current + centered pill).
   2. Staggered reveal of cards as they enter the viewport.
   Without JavaScript the nav is plain anchor links and every card is
   visible — nothing on the page depends on this file. Idempotent: the
   script tag may be included by several sections; every init is guarded.
   Theme-editor safe via shopify:section:load. */
(function () {
  'use strict';

  /* The tag may be included by several sections — execute once. */
  if (window.MilanoCollectionGuide) return;
  window.MilanoCollectionGuide = true;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Sticky nav scroll-spy ---------- */

  function initNav(nav) {
    if (!nav || nav.dataset.mcgNavInit) return;
    nav.dataset.mcgNavInit = '1';

    var list = nav.querySelector('ul');
    var links = Array.prototype.slice.call(nav.querySelectorAll('a[href^="#"]'));
    if (!links.length || !('IntersectionObserver' in window)) return;

    var pairs = [];
    links.forEach(function (link) {
      var target = document.getElementById(link.getAttribute('href').slice(1));
      if (target) pairs.push({ link: link, target: target });
    });
    if (!pairs.length) return;

    var current = null;

    function activate(link) {
      if (link === current) return;
      if (current) {
        current.classList.remove('is-active');
        current.removeAttribute('aria-current');
      }
      current = link;
      link.classList.add('is-active');
      link.setAttribute('aria-current', 'location');
      if (list && list.scrollWidth > list.clientWidth + 4) {
        var left = link.offsetLeft - (list.clientWidth - link.offsetWidth) / 2;
        if (list.scrollTo) {
          list.scrollTo({ left: left, behavior: reduceMotion ? 'auto' : 'smooth' });
        } else {
          list.scrollLeft = left;
        }
      }
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          for (var i = 0; i < pairs.length; i++) {
            if (pairs[i].target === entry.target) {
              activate(pairs[i].link);
              break;
            }
          }
        });
      },
      { rootMargin: '-25% 0px -65% 0px', threshold: 0 }
    );

    pairs.forEach(function (pair) {
      observer.observe(pair.target);
    });

    nav.mcgNavObserver = observer;
  }

  /* ---------- Card reveals ---------- */

  function initReveals(root) {
    var items = (root || document).querySelectorAll('[data-mcg-reveal]:not(.is-revealed)');
    if (!items.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(items, function (el) {
        el.classList.add('is-revealed');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.1 }
    );

    Array.prototype.forEach.call(items, function (el) {
      /* Stagger by position within the card's own grid, capped so late rows never lag. */
      var index = Array.prototype.indexOf.call(el.parentNode.children, el);
      el.style.setProperty('--mcg-delay', Math.min(Math.max(index, 0) * 55, 330) + 'ms');
      observer.observe(el);
    });
  }

  /* ---------- Expandable card groups ---------- */

  function initExpanders(root) {
    var buttons = (root || document).querySelectorAll('[data-mcg-expand]');
    Array.prototype.forEach.call(buttons, function (btn) {
      if (btn.dataset.mcgExpandInit) return;
      btn.dataset.mcgExpandInit = '1';

      var section = btn.closest('[data-section-id]') || document;
      var group = btn.getAttribute('data-mcg-expand');
      var items = Array.prototype.slice.call(section.querySelectorAll('[data-mcg-group="' + group + '"]'));

      if (!items.length) {
        btn.hidden = true;
        return;
      }

      /* Collapsed only once JS is running — no-JS visitors see every card. */
      items.forEach(function (el) {
        el.hidden = true;
      });
      btn.setAttribute('aria-expanded', 'false');

      var text = btn.querySelector('[data-mcg-expand-text]');

      btn.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        items.forEach(function (el) {
          el.hidden = open;
          if (!open) el.classList.add('is-revealed');
        });
        if (text) {
          text.textContent = open ? btn.dataset.labelOpen : btn.dataset.labelClose;
        }
        if (!open && items[0] && items[0].scrollIntoView) {
          items[0].scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest' });
        }
      });
    });
  }

  function initAll(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-mcg-nav]'), initNav);
    initReveals(scope || document);
    initExpanders(scope || document);
  }

  /* Enables the pre-reveal hidden state only when JS is actually running. */
  document.documentElement.classList.add('mcg-js');

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  /* Shopify theme editor: re-init reloaded sections, clean up removed ones. */
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    var nav = event.target.querySelector('[data-mcg-nav]');
    if (nav && nav.mcgNavObserver) nav.mcgNavObserver.disconnect();
  });
})();
